/**
 * Field catalog: maps the volunteer schema (159 fields upstream) to the small,
 * curated set the Base MVP exposes in the filter builder and table.
 *
 * `operators` lists which FilterOperator chips make sense per field; `kind`
 * tells the FilterBuilder what value-editor to render (text / select / etc).
 * Option-bearing fields point at a `refsKey` resolved against /api/refs.
 */

import type { FilterClause, FilterOperator, Refs } from "@/api/client";

export type FieldKind =
  | "text"
  | "number"
  | "date"
  | "bool"
  | "select"
  | "multiselect"
  | "celltree";

export interface FieldDef {
  /** Upstream field key, e.g. "last_name" or "branch_id". */
  field: string;
  /** Human label (RU). */
  label: string;
  kind: FieldKind;
  /** Operators offered for this field, first = default. */
  operators: FilterOperator[];
  /** When option-bearing, where to source options from refs. */
  refsKey?: string;
  /**
   * For select fields whose options are NOT in /api/refs but are loaded by
   * cascading from the selected branch_id (primary orgs / УИКи).
   */
  cascade?: "primary" | "uik";
  /** Whether this column is shown in the table by default. */
  defaultColumn?: boolean;
  /**
   * Whether this field may be written by the MASS-WRITE flows (bulk-edit dialog +
   * «Обновление из файла»). Defaults to writable; set `false` to keep the field
   * available for FILTERING (GET) while excluding it from EDITABLE_FIELDS.
   * TODO(F-bool, ревью 2026-07-07): bool-поля (is_party_loyal / is_president_loyal /
   * is_high_authority_loyal / verified) отключены для массовой записи до живой пробы
   * формата (bool vs 'y'/'n' vs 0/1) на одном id. После пробы привести
   * buildValue/normalize_values к подтверждённому формату и вернуть флаг.
   */
  bulkWritable?: boolean;
}

/** A {value,label} pair for select editors. */
export interface FieldOption {
  value: string;
  label: string;
}

const TEXT_OPS: FilterOperator[] = ["contains", "ieq", "eq", "starts", "ends", "exists", "is_empty"];

/**
 * Curated catalog. Order here drives both the field dropdown and default columns.
 * Loosely scoped to fields confirmed present on the live volunteer object.
 */
export const FIELD_CATALOG: FieldDef[] = [
  { field: "last_name", label: "Фамилия", kind: "text", operators: TEXT_OPS, defaultColumn: true },
  { field: "first_name", label: "Имя", kind: "text", operators: TEXT_OPS, defaultColumn: true },
  { field: "patronymic", label: "Отчество", kind: "text", operators: TEXT_OPS, defaultColumn: true },
  {
    field: "birthday",
    label: "Дата рождения",
    kind: "date",
    operators: ["eq", "from", "to", "between", "bday", "exists", "is_empty"],
    defaultColumn: true,
  },
  {
    field: "phone_mobile",
    label: "Телефон",
    kind: "text",
    operators: ["contains", "eq", "exists", "is_empty"],
    defaultColumn: true,
  },
  {
    field: "branch_id",
    label: "Отделение",
    kind: "select",
    operators: ["eq", "among", "exists", "is_empty"],
    refsKey: "branches",
    defaultColumn: true,
  },
  // --- Cascade & cell-tree group (kept right after branch_id) ---
  {
    field: "origin_cell_id",
    label: "Основная ячейка",
    kind: "celltree",
    // eq = ровно эта ячейка; child_of = + дочерние (включая дочерние).
    operators: ["eq", "child_of", "exists", "is_empty"],
  },
  {
    field: "cells",
    label: "Участие в ячейках",
    kind: "celltree",
    operators: ["child_of", "includes"],
  },
  // TODO(open_probe): upstream-имя поля «Первичное отделение» предварительное
  // (primary_id). Сверить с живым объектом активиста; если иное — поправить только `field`.
  {
    field: "primary_id",
    label: "Первичное отделение",
    kind: "select",
    cascade: "primary",
    operators: ["eq", "among", "exists", "is_empty"],
  },
  // TODO(open_probe): upstream-имя поля «УИК» предварительное (uik_id). Сверить
  // с живым объектом активиста; если иное — поправить только `field`.
  {
    field: "uik_id",
    label: "УИК",
    kind: "select",
    cascade: "uik",
    operators: ["eq", "among", "exists", "is_empty"],
  },
  {
    field: "address",
    label: "Адрес",
    kind: "text",
    // mob поддерживает только address__contains; ieq оставлен на случай поддержки.
    operators: ["contains", "ieq", "exists", "is_empty"],
  },
  {
    field: "loyalty",
    label: "Лояльность",
    kind: "select",
    operators: ["eq", "among", "exists", "is_empty"],
    refsKey: "loyalty",
    defaultColumn: true,
  },
  {
    field: "membership",
    label: "Членство",
    kind: "select",
    operators: ["eq", "among", "exists", "is_empty"],
    refsKey: "membership",
    defaultColumn: true,
  },
  {
    field: "readiness",
    label: "Готовность",
    kind: "select",
    operators: ["eq", "among"],
  },
  {
    field: "readiness_region",
    label: "Готовность (регион)",
    kind: "select",
    operators: ["eq", "among"],
  },
  // Признаки, которые mob-api выставляет после передачи явки в ЕДГ-лотерею
  // (проверено вживую 2026-09-11): cfabzb = «Рег онлайн», bfabzk = true.
  // mob принимает по cfabzb только __eq (among → 400 «not allowed»).
  {
    field: "cfabzb",
    label: "Участие ЕДГ-2026 (явка онлайн)",
    kind: "select",
    operators: ["eq"],
    bulkWritable: false,
  },
  {
    field: "bfabzk",
    label: "Участие ЕДГ-2026 сводная явка",
    kind: "bool",
    operators: ["eq"],
    bulkWritable: false,
  },
  {
    field: "status",
    label: "Статус",
    kind: "multiselect",
    operators: ["among", "includes", "exists", "is_empty"],
    refsKey: "status",
  },
  {
    field: "target_group_ids",
    label: "Целевые группы",
    kind: "multiselect",
    operators: ["among", "includes"],
    refsKey: "targetGroups",
  },
  {
    field: "citizen_category_ids",
    label: "Категории граждан",
    kind: "multiselect",
    operators: ["among", "includes"],
    refsKey: "citizenCategories",
  },
  // bulkWritable: false — bool-поля отключены для МАССОВОЙ ЗАПИСИ до живой пробы
  // формата записи (bool vs 'y'/'n' vs 0/1); фильтрация (GET) по ним сохранена.
  // См. F-bool ревью 2026-07-07 и TODO в FieldDef.bulkWritable.
  {
    field: "is_party_loyal",
    label: "Лоялен партии",
    kind: "bool",
    operators: ["eq"],
    bulkWritable: false,
  },
  {
    field: "is_president_loyal",
    label: "Лоялен президенту",
    kind: "bool",
    operators: ["eq"],
    bulkWritable: false,
  },
  {
    field: "verified",
    label: "Верифицирован",
    kind: "bool",
    operators: ["eq"],
    bulkWritable: false,
  },
];

export const FIELD_BY_KEY: Record<string, FieldDef> = Object.fromEntries(
  FIELD_CATALOG.map((f) => [f.field, f]),
);

/**
 * Build the facet filter clause for a set of selected values, choosing the operator
 * from the field's kind: `multiselect` array fields (target_group_ids, status, …) must
 * use `among` even for a single value — `eq` is invalid for them upstream (F22).
 * Scalar `select` fields use `eq` for one value, `among` for several.
 */
export function facetValuesClause(field: string, values: string[]): FilterClause {
  const def = FIELD_BY_KEY[field];
  if (def && def.kind === "multiselect") {
    return { field, operator: "among", value: values };
  }
  return values.length === 1
    ? { field, operator: "eq", value: values[0] }
    : { field, operator: "among", value: values };
}

/** Single-value facet clause (used for per-option live counts) — same operator rule. */
export function facetValueClause(field: string, value: unknown): FilterClause {
  const def = FIELD_BY_KEY[field];
  if (def && def.kind === "multiselect") {
    return { field, operator: "among", value: [value] };
  }
  return { field, operator: "eq", value };
}

/**
 * Fields allowed in the bulk "set one value for all selected" dialog (Wave 4a).
 * Whitelist per MOB_API_REVERSE «Каталог» / PUT bulk semantics — NO text identity
 * (ФИО / телефон / адрес) in 4a. Each entry must exist in FIELD_CATALOG so the
 * shared ValueEditor can render the right picker.
 *
 * is_high_authority_loyal is added conditionally only if a FIELD_CATALOG entry for
 * it appears; today the catalog carries is_party_loyal / is_president_loyal / verified.
 *
 * TODO(F-bool, ревью 2026-07-07): bool-поля (is_party_loyal / is_president_loyal /
 * is_high_authority_loyal / verified) временно ОТКЛЮЧЕНЫ для массовой записи через
 * bulkWritable:false в FIELD_CATALOG — до живой пробы формата записи (bool vs 'y'/'n'
 * vs 0/1) на одном id. Фильтрация (GET) по ним не затронута. После пробы привести
 * buildValue (FieldValuePicker) / normalize_values (бэк) к подтверждённому формату и
 * снять флаг, чтобы поля вернулись в набор массовой записи.
 */
export const EDITABLE_FIELDS: ReadonlySet<string> = new Set(
  [
    "loyalty",
    "membership",
    "readiness",
    "readiness_region",
    "status",
    "target_group_ids",
    "citizen_category_ids",
    "verified",
    "is_party_loyal",
    "is_president_loyal",
    "is_high_authority_loyal",
  ]
    .filter((f) => f in FIELD_BY_KEY)
    // bulkWritable:false исключает поле только из МАССОВОЙ ЗАПИСИ (см. TODO F-bool выше).
    .filter((f) => FIELD_BY_KEY[f]?.bulkWritable !== false),
);

/** FieldDefs (in catalog order) that are editable in the bulk-update dialog. */
export const EDITABLE_FIELD_DEFS: FieldDef[] = FIELD_CATALOG.filter((f) =>
  EDITABLE_FIELDS.has(f.field),
);

/** Human label for an operator (RU), used in the operator dropdown. */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: "равно",
  ieq: "равно (без регистра)",
  contains: "содержит",
  includes: "включает",
  starts: "начинается с",
  ends: "заканчивается на",
  exists: "заполнено",
  is_empty: "пусто",
  among: "одно из",
  bday: "день рождения",
  gte: "≥",
  lte: "≤",
  from: "от",
  to: "до",
  between: "между (даты)",
  between_numeric: "между (числа)",
  child_of: "входит в",
  in: "в списке",
};

/** Operators whose value editor should be hidden (no value needed). */
export const VALUELESS_OPERATORS: ReadonlySet<FilterOperator> = new Set(["exists", "is_empty"]);

/**
 * Resolve {value,label} options for an option-bearing field from refs.
 * Handles the heterogeneous shapes in /api/refs:
 *   - string[]                       (status, loyalty)
 *   - {code: label} dict             (membership)
 *   - [{id, name|title}]             (branches, targetGroups, citizenCategories)
 */
export function resolveOptions(field: FieldDef, refs: Refs | undefined): FieldOption[] {
  // readiness / readiness_region are fixed strings (NOT keys in /api/refs), and they
  // differ: readiness = Да/Нет only; readiness_region = Да/Нет/На проверке (verified
  // on the live API: readiness=На проверке returns 0).
  if (field.field === "readiness") return readinessOptions();
  if (field.field === "readiness_region") return readinessRegionOptions();
  if (field.field === "cfabzb") return edgOnlineTurnoutOptions();
  if (!refs || !field.refsKey) return [];

  const raw = (refs as Record<string, unknown>)[field.refsKey];
  if (Array.isArray(raw)) {
    return raw
      .map((item): FieldOption | null => {
        if (typeof item === "string") return { value: item, label: item };
        if (item && typeof item === "object") {
          const obj = item as Record<string, unknown>;
          if (obj.is_deleted === true || obj.disabled === true) return null;
          const id = obj.id;
          const label = (obj.name ?? obj.title ?? String(id ?? "")) as string;
          const value = id != null ? String(id) : label;
          return { value, label };
        }
        return null;
      })
      .filter((x): x is FieldOption => x != null);
  }
  if (raw && typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>).map(([code, label]) => ({
      value: code,
      label: String(label),
    }));
  }
  return [];
}

function readinessOptions(): FieldOption[] {
  return [
    { value: "Да", label: "Да" },
    { value: "Нет", label: "Нет" },
  ];
}

function readinessRegionOptions(): FieldOption[] {
  return [
    { value: "Да", label: "Да" },
    { value: "Нет", label: "Нет" },
    { value: "На проверке", label: "На проверке" },
  ];
}

/** cfabzb («Участие ЕДГ-2026 (явка онлайн)») — фиксированные choices мob-api. */
function edgOnlineTurnoutOptions(): FieldOption[] {
  return ["Гео-СМС", "QR-код", "Рег онлайн", "Нет"].map((v) => ({ value: v, label: v }));
}

/** Boolean filter fields (is_*, verified) — the wire wants 'y'/'n', NOT true/false. */
export function boolOptions(): FieldOption[] {
  return [
    { value: "y", label: "Да" },
    { value: "n", label: "Нет" },
  ];
}
